import type { InstrumentRow, MaterialDetail, MaterialDraw } from '@api/replenishment-engine';
import { GenericChartCard } from '@components/ui/Chart';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { Badge, Button, Group, Stack, Text } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { useMemo, type FC, type ReactElement } from 'react';

import ExplainWindow from '../ExplainWindow';
import { addDaysKey, dayLabel, localDateKey, whenLabel } from './dates';
import { STATUS_COLOR } from './status';
import { ApproveGate } from './ApproveGate';
import { Lines } from './tables';

type Props = {
    detail: MaterialDetail | null;
    /** The objective's window, to say how many of the draws fall inside it. */
    window?: { start: string | null; end: string | null };
    onClose: () => void;
    onApprove: (ids: string[]) => void;
    onSave?: (ids: string[]) => void;
    onOpenSku: (productId: string) => void;
};

type Point = { day: string; label: string; stock: number; floor: number; draw: number; landing: number };

const n = (v: number, maxDecimals = 1): string => formatCount(v, { maxDecimals });

const u = (d: MaterialDetail): string => (d.unit ? ` ${d.unit}` : '');

/**
 * The material's story at one factory: what is on the floor (and in stage form), what the
 * planned runs draw of it in start order, what the floor covers before an order has to land,
 * and the orders and transfers that bring more, each with the runs it unlocks.
 */
const MaterialWindow: FC<Props> = ({ detail, window, onClose, onApprove, onSave, onOpenSku }) => {
    const today = localDateKey();
    const points = useMemo<Point[]>(() => {
        if (!detail) return [];
        const s = detail.series;
        const count = Math.min(s.stock.length, 120);
        const out: Point[] = [];
        for (let i = 0; i < count; i += 1) {
            const day = addDaysKey(s.from, i);
            out.push({ day, label: dayLabel(day), stock: Math.round(s.stock[i] * 10) / 10, floor: Math.round(s.floor[i] * 10) / 10, draw: Math.round(s.draw[i] * 10) / 10, landing: Math.round(s.landing[i] * 10) / 10 });
        }
        return out;
    }, [detail]);
    const drawColumns = useMemo<DataSheetColumn<MaterialDraw>[]>(
        () => [
            { key: 'sku', header: 'Run', width: 240, sticky: true, sortable: true, valueAccessor: (r) => r.sku, render: (_v, r): ReactElement => <Lines lines={[{ text: r.sku, strong: true, sub: `${n(r.runQty, 0)} units${r.parts > 1 ? ` · part ${r.part} of ${r.parts}` : ''}${r.seq > 1 ? ` · replenishment ${r.seq}` : ''}` }]} /> },
            { key: 'start', header: 'Starts', width: 130, sortable: true, valueAccessor: (r) => r.startDate ?? '', render: (_v, r): ReactElement => <Lines lines={[{ text: r.startDate ? whenLabel(r.startDate, today) : '—', strong: true, sub: r.landDate ? `lands ${dayLabel(r.landDate)}` : '' }]} /> },
            { key: 'need', header: 'Draws', width: 120, align: 'right', sortable: true, valueAccessor: (r) => r.needQty, render: (_v, r): ReactElement => <Lines lines={[{ text: n(r.needQty), strong: true, sub: r.stageQty > 0 ? `+ ${n(r.stageQty, 0)} stage pcs` : '' }]} /> },
            { key: 'floor', header: 'From floor', width: 110, align: 'right', render: (_v, r): ReactElement => <Text size="sm">{r.floorQty > 0 ? n(r.floorQty) : '—'}</Text> },
            { key: 'moved', header: 'By transfer', width: 110, align: 'right', render: (_v, r): ReactElement => <Text size="sm">{r.movedQty > 0 ? n(r.movedQty) : '—'}</Text> },
            { key: 'ordered', header: 'By order', width: 110, align: 'right', render: (_v, r): ReactElement => <Text size="sm" c={r.orderedQty > 0 ? 'orange' : undefined}>{r.orderedQty > 0 ? n(r.orderedQty) : '—'}</Text> },
            { key: 'short', header: 'Short', width: 90, align: 'right', render: (_v, r): ReactElement => <Text size="sm" c={r.shortQty > 0 ? 'red' : undefined}>{r.shortQty > 0 ? n(r.shortQty) : '—'}</Text> },
            { key: 'status', header: 'Run status', width: 120, render: (_v, r): ReactElement => (r.status ? <Badge size="xs" variant="light" color={STATUS_COLOR[r.status]} tt="none">{r.status.toLowerCase()}</Badge> : <Text size="sm" c="dimmed">—</Text>) },
        ],
        [today],
    );
    const landingColumns = useMemo<DataSheetColumn<InstrumentRow>[]>(
        () => [
            { key: 'what', header: 'Order', width: 220, sticky: true, render: (_v, r): ReactElement => <Lines lines={[{ text: r.type === 'TRANSFER' ? `Transfer from ${r.fromFacilityId}` : r.vendorName ?? (r.choicePending ? 'vendor to choose' : r.vendorId ?? 'purchase order'), strong: true, sub: r.documentCode ?? '' }]} /> },
            { key: 'qty', header: 'Quantity', width: 110, align: 'right', sortable: true, valueAccessor: (r) => r.qty, render: (_v, r): ReactElement => <Text size="sm" fw={600}>{n(r.qty)}</Text> },
            { key: 'place', header: 'Place / dispatch by', width: 150, sortable: true, valueAccessor: (r) => r.placeBy, render: (_v, r): ReactElement => <Lines lines={[{ text: whenLabel(r.placeBy, today), late: r.placeBy <= today && r.status === 'PROPOSED', strong: true, sub: r.leadDays != null ? `${r.leadDays}d lead` : '' }]} /> },
            { key: 'lands', header: 'Lands', width: 110, sortable: true, valueAccessor: (r) => r.landDate, render: (_v, r): ReactElement => <Text size="sm" fw={600}>{dayLabel(r.landDate)}</Text> },
            { key: 'unlocks', header: 'Unlocks', width: 300, render: (_v, r): ReactElement => <Lines lines={[{ text: r.feeds.length ? `${r.feeds.length} run${r.feeds.length === 1 ? '' : 's'}` : 'nothing pegged', sub: r.feeds.slice(0, 4).map((f) => `${f.sku} ${dayLabel(f.placeBy)}${f.inputQty != null ? ` · ${n(f.inputQty)}` : ''}`).join(' · ') + (r.feeds.length > 4 ? ` · +${r.feeds.length - 4} more` : '') }]} /> },
            { key: 'status', header: 'Status', width: 110, render: (_v, r): ReactElement => <Badge size="xs" variant="light" color={STATUS_COLOR[r.status]} tt="none">{r.status.toLowerCase()}</Badge> },
        ],
        [today],
    );
    if (!detail) return null;
    const unit = u(detail);
    const stageOnHand = detail.stage.reduce((s, x) => s + x.onHand, 0);
    // The floor (with stage pieces and what is on the book) carries every run until the first one that needs an order to land.
    const firstOrdered = detail.draws.findIndex((d) => d.orderedQty > 0 || d.shortQty > 0);
    const coveredRuns = firstOrdered === -1 ? detail.draws.length : firstOrdered;
    const coveredThrough = coveredRuns > 0 ? detail.draws[coveredRuns - 1].startDate : null;
    const inWindow = window?.start && window.end ? detail.draws.filter((d) => d.startDate && d.startDate >= window.start! && d.startDate <= window.end!).length : null;
    const open = detail.landings.filter((l) => l.status === 'PROPOSED').map((l) => l.id);
    const nextLanding = detail.landings.find((l) => l.status !== 'RECEIVED' && l.status !== 'CLOSED');

    // The story, in one sentence: these SKUs are made in the period, so these runs draw this
    // much of the material; the floor, stage pieces and moves cover part; the rest is ordered.
    const story = (() => {
        const inPeriod = window?.start && window.end ? detail.draws.filter((d) => d.startDate && d.startDate >= window.start! && d.startDate <= window.end!) : detail.draws;
        if (inPeriod.length === 0) return null;
        const skus = new Set(inPeriod.map((d) => d.sku));
        const units = inPeriod.reduce((s, d) => s + d.runQty, 0);
        const need = inPeriod.reduce((s, d) => s + d.needQty, 0);
        const covered = inPeriod.reduce((s, d) => s + d.floorQty + d.stageQty + d.movedQty, 0);
        const ordered = inPeriod.reduce((s, d) => s + d.orderedQty, 0);
        const short = inPeriod.reduce((s, d) => s + d.shortQty, 0);
        const names = [...skus].slice(0, 3).join(', ') + (skus.size > 3 ? ` and ${skus.size - 3} more` : '');
        return (
            <Text size="sm">
                To make <b>{n(units, 0)} units</b> of {names} {window?.start ? 'in this period' : 'in the plan'}, the runs draw <b>{n(need)}{unit}</b>. On hand covers {n(covered)}{unit}
                {ordered > 0 ? <>, so <b>{n(ordered)}{unit}</b> is ordered</> : ''}
                {short > 0 ? <>, and {n(short)}{unit} has no source in time</> : ''}.
            </Text>
        );
    })();

    const banner = (
        <div>
            {story}
            <Group gap="lg" wrap="wrap" mt={story ? 6 : 0}>
                <Text size="sm">
                    On the floor <b>{n(detail.onHand)}{unit}</b>
                    {stageOnHand > 0 ? ` · as stage pieces ${detail.stage.map((x) => `${n(x.onHand, 0)} ${x.materialName ?? x.materialId}`).join(', ')}` : ''}
                    {detail.inboundOnBook > 0 ? ` · on the book ${n(detail.inboundOnBook)}${unit}${detail.firstInboundDate ? ` from ${dayLabel(detail.firstInboundDate)}` : ''}` : ''}
                    {detail.committedDraw > 0 ? ` · issued production takes ${n(detail.committedDraw)}${unit}` : ''}
                </Text>
                <Text size="sm">
                    {detail.draws.length === 0
                        ? 'No planned run draws it.'
                        : coveredRuns === detail.draws.length
                          ? `The floor covers all ${detail.draws.length} planned run${detail.draws.length === 1 ? '' : 's'}.`
                          : coveredRuns === 0
                            ? `Nothing on the floor for the ${detail.draws.length} planned run${detail.draws.length === 1 ? '' : 's'}: the first waits on an order.`
                            : `The floor covers the first ${coveredRuns} run${coveredRuns === 1 ? '' : 's'} (through ${dayLabel(coveredThrough)}); ${detail.draws.length - coveredRuns} wait on an order.`}
                    {inWindow != null ? ` ${inWindow} in this window.` : ''}
                </Text>
                <Text size="sm">
                    {detail.runsOutOn ? (
                        <>
                            Runs out <b>{whenLabel(detail.runsOutOn, today)}</b> at the planned draw
                        </>
                    ) : (
                        'Does not run out inside the horizon'
                    )}
                    {nextLanding ? ` · next landing ${n(nextLanding.qty)}${unit} on ${dayLabel(nextLanding.landDate)} (${nextLanding.status.toLowerCase()})` : ''}
                </Text>
            </Group>
        </div>
    );

    return (
        <ExplainWindow
            opened
            wide
            onClose={onClose}
            title={detail.materialName ?? detail.materialId}
            subtitle={`${detail.materialId} · drawn at ${detail.siteId}${detail.landsAt ? ` · orders land at ${detail.landsAt}` : ''}${detail.unit ? ` · in ${detail.unit}` : ''}`}
            banner={banner}
            footer={
                open.length > 0 ? (
                    <Group justify="flex-end" p="sm">
                        {onSave && (
                            <Button size="xs" variant="subtle" color="grape" title="Keep the order numbers as they stand, without creating any document" onClick={() => onSave(open)}>
                                Save
                            </Button>
                        )}
                        <ApproveGate>
                            <Button size="xs" onClick={() => onApprove(open)}>
                                Approve {open.length === 1 ? 'the order' : `${open.length} orders`}
                            </Button>
                        </ApproveGate>
                    </Group>
                ) : undefined
            }
        >
            <Stack gap="md" p="md">
                {points.length > 0 && (
                    <GenericChartCard<Point>
                        kind="line"
                        title="Next 120 days"
                        subtitle="Floor with what is on the book; planned draws take from it, planned landings add to it"
                        data={points}
                        dimensionKey="label"
                        dimensionType="category"
                        series={[
                            { id: 'stock', key: 'stock', label: 'Stock after planned runs' },
                            { id: 'floor', key: 'floor', label: 'Floor on the book', variant: 'dashed' },
                            { id: 'draw', key: 'draw', label: 'Planned draw', renderAs: 'bar' },
                            { id: 'landing', key: 'landing', label: 'Planned landing', renderAs: 'bar' },
                        ]}
                        height={220}
                    />
                )}
                <Stack gap={4}>
                    <Text fw={700} size="sm">
                        Runs that draw it, in start order
                    </Text>
                    <DataSheet<MaterialDraw> data={detail.draws} columns={drawColumns} getRowKey={(r) => r.requirementId} rowHeight={48} hideHeaderBar maxHeight="auto" onRowClick={(r) => onOpenSku(r.productId)} />
                </Stack>
                <Stack gap={4}>
                    <Text fw={700} size="sm">
                        Orders and transfers bringing more
                    </Text>
                    {detail.landings.length === 0 ? (
                        <Text size="sm" c="dimmed">
                            None planned: the floor and what is on the book carry every run.
                        </Text>
                    ) : (
                        <DataSheet<InstrumentRow> data={detail.landings} columns={landingColumns} getRowKey={(r) => r.id} rowHeight={48} hideHeaderBar maxHeight="auto" />
                    )}
                </Stack>
            </Stack>
        </ExplainWindow>
    );
};

export default MaterialWindow;
